import { useEffect, useState, type FormEvent } from 'react';
import { describeError, useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Empty, ErrorBox, Kbd, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { CampaignPage } from './types.ts';
import './content.css';

/**
 * Campaign pages (docs/campaigns-and-widgets.md): one evergreen URL per campaign and site, a yearly edition with
 * new dates, stores, intro, advice, key dates and FAQ. Widgets add sold and house content to their regions.
 */
export function CampaignPageList() {
  const changes = useChanges();
  const { navigate } = useRouter();
  const list = useOp<{ campaigns: CampaignPage[] }>('admin.campaignPages.list', {}, changes);
  const [cursor, setCursor] = useState(0);
  const rows = list.data?.campaigns ?? [];
  useKeys({
    j: () => setCursor((c) => step(c, 1, rows.length)),
    k: () => setCursor((c) => step(c, -1, rows.length)),
    enter: () => rows[cursor] && navigate(`/campaign-pages/${rows[cursor]!.ref}`),
    n: () => navigate('/campaign-pages/new'),
  });
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Campaign pages</h1>
          <p className="muted">
            Black Friday, January sales, Student deals: one page per campaign and country with an evergreen URL. Each year only the dates move; stores, texts and
            FAQ carry over. Widgets add highlights and sold positions.
          </p>
        </div>
        <Link className="btn btn-primary" to="/campaign-pages/new">
          New campaign page <Kbd>n</Kbd>
        </Link>
      </header>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && !list.error ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No campaign pages yet.</Empty> : null}
      {rows.length ? (
        <table className="table" aria-label="Campaign pages">
          <thead>
            <tr>
              <th scope="col">Campaign</th>
              <th scope="col">Country</th>
              <th scope="col">Dates</th>
              <th scope="col">Status</th>
              <th scope="col" className="num">
                Stores
              </th>
              <th scope="col">Needs attention</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => navigate(`/campaign-pages/${row.ref}`)} onMouseEnter={() => setCursor(index)}>
                <td>
                  <strong>{row.name}</strong>
                  <div className="small muted">/{row.slug}</div>
                </td>
                <td>{row.marketCode}</td>
                <td className="small">
                  {formatDate(row.startsAt)} – {formatDate(row.endsAt)}
                </td>
                <td>
                  <span className={`badge ${row.status === 'live' ? 'badge-ok' : ''}`}>{row.status}</span>
                </td>
                <td className="num">{row.merchants.length}</td>
                <td className="small">{row.issues.length ? <span className="text-warn">{row.issues.join(' · ')}</span> : <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

interface Draft {
  marketCode: string;
  slug: string;
  name: string;
  kind: CampaignPage['kind'];
  status: CampaignPage['status'];
  startsAt: string;
  endsAt: string;
  intro: string;
  advice: string;
  timeline: { label: string; date: string }[];
  faq: { question: string; answer: string }[];
  merchantRefs: string[];
}

const day = (iso: string) => iso.slice(0, 10);

function fromCampaign(c: CampaignPage): Draft {
  return {
    marketCode: c.marketCode,
    slug: c.slug,
    name: c.name,
    kind: c.kind,
    status: c.status,
    startsAt: day(c.startsAt),
    endsAt: day(c.endsAt),
    intro: c.intro,
    advice: c.advice,
    timeline: c.timeline.map((t) => ({ label: t.label, date: day(t.date) })),
    faq: c.faq,
    merchantRefs: c.merchants.map((m) => m.ref),
  };
}

const EMPTY: Draft = { marketCode: 'GB', slug: '', name: '', kind: 'seasonal', status: 'draft', startsAt: '', endsAt: '', intro: '', advice: '', timeline: [], faq: [], merchantRefs: [] };

export function CampaignPageEditor({ campaignRef }: { campaignRef: string | null }) {
  const changes = useChanges();
  const toast = useToast();
  const { navigate } = useRouter();
  const existing = useOp<CampaignPage>('admin.campaignPages.get', { ref: campaignRef }, changes, campaignRef !== null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const merchants = useOp<{ rows: { ref: string; name: string; slug: string; shownOn: number }[] }>('admin.merchants.list', { market: draft.marketCode, limit: 200, filter: 'all' }, 0, draft.marketCode.length === 2);
  useEffect(() => {
    if (existing.data) setDraft(fromCampaign(existing.data));
  }, [existing.data]);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const canEdit = campaignRef === null || existing.data?.canEdit === true;

  const save = async (event?: FormEvent) => {
    event?.preventDefault();
    setSaving(true);
    const common = {
      name: draft.name,
      kind: draft.kind,
      startsAt: draft.startsAt,
      endsAt: draft.endsAt,
      intro: draft.intro,
      advice: draft.advice,
      merchantRefs: draft.merchantRefs,
    };
    if (campaignRef === null) {
      const created = await runWrite<{ ref: string }>(toast, 'admin.campaignPages.create', { ...common, marketCode: draft.marketCode, slug: draft.slug }, 'Campaign page created as a draft');
      if (created) navigate(`/campaign-pages/${created.ref}`);
    } else {
      await runWrite(toast, 'admin.campaignPages.update', { ...common, ref: campaignRef, status: draft.status, timeline: draft.timeline.filter((t) => t.label && t.date), faq: draft.faq.filter((f) => f.question && f.answer) }, 'Saved');
    }
    setSaving(false);
  };
  const nextEdition = async () => {
    if (!campaignRef) return;
    await runWrite<{ startsAt: string }>(toast, 'admin.campaignPages.nextEdition', { ref: campaignRef }, (r) => `Next edition from ${formatDate(r.startsAt)}, set to draft: check the dates, then set it live`);
  };
  useKeys({ escape: () => navigate('/campaign-pages') });

  if (campaignRef && existing.error) return <ErrorBox error={existing.error} onRetry={existing.reload} />;
  if (campaignRef && !existing.data) return <Loading />;
  const allMerchants = merchants.data?.rows ?? [];
  const toggleMerchant = (ref: string) => set('merchantRefs', draft.merchantRefs.includes(ref) ? draft.merchantRefs.filter((r) => r !== ref) : [...draft.merchantRefs, ref]);

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="small">
            <Link to="/campaign-pages">Campaign pages</Link>
          </p>
          <h1>{campaignRef ? draft.name || 'Campaign page' : 'New campaign page'}</h1>
          {existing.data?.issues.length ? <p className="text-warn small">{existing.data.issues.join(' · ')}</p> : null}
        </div>
        {campaignRef && canEdit && draft.kind === 'seasonal' ? (
          <button type="button" className="btn" onClick={() => void nextEdition()}>
            Start next year’s edition
          </button>
        ) : null}
      </header>
      <form onSubmit={(e) => void save(e)}>
        <fieldset disabled={!canEdit || saving}>
          <div className="form-grid">
            <label>
              Name
              <input required maxLength={120} value={draft.name} onChange={(e) => set('name', e.target.value)} placeholder="Black Friday" />
            </label>
            <label>
              URL slug
              <input required disabled={campaignRef !== null} pattern="[a-z0-9][a-z0-9-]*" value={draft.slug} onChange={(e) => set('slug', e.target.value)} placeholder="black-friday" />
            </label>
            <label>
              Country
              <input required disabled={campaignRef !== null} maxLength={2} value={draft.marketCode} onChange={(e) => set('marketCode', e.target.value.toUpperCase())} />
            </label>
            <label>
              Kind
              <select value={draft.kind} onChange={(e) => set('kind', e.target.value as Draft['kind'])}>
                <option value="seasonal">Seasonal (returns every year)</option>
                <option value="theme">Theme (evergreen)</option>
                <option value="brand">Brand week</option>
              </select>
            </label>
            {campaignRef ? (
              <label>
                Status
                <select value={draft.status} onChange={(e) => set('status', e.target.value as Draft['status'])}>
                  <option value="draft">Draft (not on the sites)</option>
                  <option value="live">Live</option>
                </select>
              </label>
            ) : null}
            <label>
              Starts
              <input type="date" required value={draft.startsAt} onChange={(e) => set('startsAt', e.target.value)} />
            </label>
            <label>
              Ends
              <input type="date" required value={draft.endsAt} onChange={(e) => set('endsAt', e.target.value)} />
            </label>
            <label className="c-wide">
              Intro (first paragraph under the heading)
              <textarea rows={3} maxLength={4000} value={draft.intro} onChange={(e) => set('intro', e.target.value)} />
            </label>
            <label className="c-wide">
              Our advice (buy now or wait?)
              <textarea rows={3} maxLength={4000} value={draft.advice} onChange={(e) => set('advice', e.target.value)} />
            </label>
          </div>

          {campaignRef ? (
            <>
              <h2 className="section-title">Key dates</h2>
              {draft.timeline.map((entry, index) => (
                <div className="inline-field" key={index}>
                  <input type="date" value={entry.date} onChange={(e) => set('timeline', draft.timeline.map((t, i) => (i === index ? { ...t, date: e.target.value } : t)))} />
                  <input value={entry.label} placeholder="What happens" onChange={(e) => set('timeline', draft.timeline.map((t, i) => (i === index ? { ...t, label: e.target.value } : t)))} />
                  <button type="button" className="btn btn-small" onClick={() => set('timeline', draft.timeline.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-small" onClick={() => set('timeline', [...draft.timeline, { label: '', date: draft.startsAt }])}>
                Add key date
              </button>

              <h2 className="section-title">Questions and answers</h2>
              {draft.faq.map((item, index) => (
                <div className="form-grid" key={index}>
                  <label className="c-wide">
                    Question
                    <input value={item.question} onChange={(e) => set('faq', draft.faq.map((f, i) => (i === index ? { ...f, question: e.target.value } : f)))} />
                  </label>
                  <label className="c-wide">
                    Answer
                    <textarea rows={2} value={item.answer} onChange={(e) => set('faq', draft.faq.map((f, i) => (i === index ? { ...f, answer: e.target.value } : f)))} />
                  </label>
                  <button type="button" className="btn btn-small" onClick={() => set('faq', draft.faq.filter((_, i) => i !== index))}>
                    Remove question
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-small" onClick={() => set('faq', [...draft.faq, { question: '', answer: '' }])}>
                Add question
              </button>
            </>
          ) : null}

          <h2 className="section-title">Stores in this campaign ({draft.merchantRefs.length})</h2>
          {merchants.error ? <p className="text-warn small">{describeError(merchants.error)}</p> : null}
          <div className="chip-list">
            {allMerchants.map((m) => (
              <label key={m.ref} className={`chip-toggle ${draft.merchantRefs.includes(m.ref) ? 'on' : ''}`}>
                <input type="checkbox" checked={draft.merchantRefs.includes(m.ref)} onChange={() => toggleMerchant(m.ref)} />
                {m.name}
              </label>
            ))}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {saving ? 'Saving…' : campaignRef ? 'Save' : 'Create draft'}
            </button>
            <Link className="btn" to="/campaign-pages">
              Back <Kbd>Esc</Kbd>
            </Link>
          </div>
        </fieldset>
      </form>
    </section>
  );
}
